import axios from "axios";
import { baseURL } from "../utils/StaticVariables";

/**
 * Normalizes the base API URL to ensure a trailing slash.
 */
const getApiBaseUrl = () => {
  const url = baseURL || "/";
  return url.endsWith("/") ? url : `${url}/`;
};

const authHeaders = (token) => ({
  Authorization: token ? `Bearer ${token}` : undefined,
});

/** Endpoint for IncidentVideosTab (GET lost-found/events). */
export const lostFoundEventsEndpoint = () => `${getApiBaseUrl()}lost-found/events`;

/** Endpoint for IncidentVideosTab's status changes (POST lost-found/events/{event_id}/resolve). */
export const lostFoundResolveEndpoint = () => `${getApiBaseUrl()}lost-found/events/{event_id}/resolve`;

/**
 * Workspace cameras with their lost & found config.
 * Endpoint: GET /lost-found/cameras
 *
 * @param {string} token - Bearer auth token
 * @param {boolean} enabledOnly - only cameras with Lost & Found enabled
 * @returns {Promise<Array>} List of cameras
 */
export const fetchLostFoundCameras = async (token, enabledOnly = true) => {
  const response = await axios.get(`${getApiBaseUrl()}lost-found/cameras`, {
    headers: authHeaders(token),
    params: { enabled_only: enabledOnly },
  });
  return response.data;
};

/**
 * Event counters for the tab header.
 * Endpoint: GET /lost-found/events/stats
 */
export const fetchLostFoundEventStats = async (token) => {
  const response = await axios.get(`${getApiBaseUrl()}lost-found/events/stats`, { headers: authHeaders(token) });
  return response.data;
};

/**
 * The found gallery.
 * Endpoint: GET /lost-found/found-items
 *
 * @param {Object} params - camera_id, class_name, color, status, start, end, page, per_page
 * @returns {Promise<Object>} { total, page, per_page, items }
 */
export const fetchFoundItems = async (params = {}, token) => {
  const response = await axios.get(`${getApiBaseUrl()}lost-found/found-items`, {
    headers: authHeaders(token),
    params,
  });
  return response.data;
};

/**
 * Endpoint: PATCH /lost-found/found-items/{found_id}
 */
export const setFoundItemStatus = async (foundId, status, token) => {
  const response = await axios.patch(
    `${getApiBaseUrl()}lost-found/found-items/${encodeURIComponent(foundId)}`,
    { status },
    { headers: authHeaders(token) }
  );
  return response.data;
};

/**
 * Lost reports.
 * Endpoint: GET /lost-found/lost-items
 *
 * @returns {Promise<Object>} { total, page, per_page, items }
 */
export const fetchLostItems = async (params = {}, token) => {
  const response = await axios.get(`${getApiBaseUrl()}lost-found/lost-items`, {
    headers: authHeaders(token),
    params,
  });
  return response.data;
};

/**
 * Endpoint: GET /lost-found/lost-items/{lost_id}
 */
export const fetchLostItem = async (lostId, token) => {
  const response = await axios.get(`${getApiBaseUrl()}lost-found/lost-items/${encodeURIComponent(lostId)}`, {
    headers: authHeaders(token),
  });
  return response.data;
};

/**
 * Files a lost report (multipart: photo and/or description).
 * Endpoint: POST /lost-found/lost-items
 *
 * @param {Object} report - { photo: File|null, description, reporterName, reporterEmail, className, lostAfter }
 */
export const reportLostItem = async (report, token) => {
  const form = new FormData();
  if (report.photo) form.append("photo", report.photo);
  const fields = {
    description: report.description,
    reporter_name: report.reporterName,
    reporter_email: report.reporterEmail,
    class_name: report.className,
    lost_after: report.lostAfter,
  };
  Object.entries(fields).forEach(([key, value]) => {
    if (value) form.append(key, value);
  });
  const response = await axios.post(`${getApiBaseUrl()}lost-found/lost-items`, form, {
    headers: authHeaders(token),
  });
  return response.data;
};

/**
 * Endpoint: PATCH /lost-found/lost-items/{lost_id}
 */
export const setLostItemStatus = async (lostId, status, token) => {
  const response = await axios.patch(
    `${getApiBaseUrl()}lost-found/lost-items/${encodeURIComponent(lostId)}`,
    { status },
    { headers: authHeaders(token) }
  );
  return response.data;
};

/**
 * Found items ranked against a lost report.
 * Endpoint: GET /lost-found/lost-items/{lost_id}/matches
 *
 * @returns {Promise<Object>} { lost_id, count, matches }
 */
export const fetchLostItemMatches = async (lostId, token, params = { persist: true }) => {
  const response = await axios.get(
    `${getApiBaseUrl()}lost-found/lost-items/${encodeURIComponent(lostId)}/matches`,
    { headers: authHeaders(token), params }
  );
  return response.data;
};

/**
 * Endpoint: POST /lost-found/matches/confirm
 */
export const confirmLostFoundMatch = async ({ lostId, foundId, score }, token) => {
  const response = await axios.post(
    `${getApiBaseUrl()}lost-found/matches/confirm`,
    { lost_id: lostId, found_id: foundId, score: score ?? 1.0 },
    { headers: authHeaders(token) }
  );
  return response.data;
};

/**
 * Endpoint: DELETE /lost-found/matches?lost_id&found_id
 */
export const rejectLostFoundMatch = async ({ lostId, foundId }, token) => {
  const response = await axios.delete(`${getApiBaseUrl()}lost-found/matches`, {
    headers: authHeaders(token),
    params: { lost_id: lostId, found_id: foundId },
  });
  return response.data;
};

/**
 * KPIs, trend, heatmap, per camera / class, funnel.
 * Endpoint: GET /lost-found/analytics/summary
 *
 * @param {Object} params - { days, camera_id, tz }
 */
export const fetchLostFoundSummary = async (params = {}, token) => {
  const response = await axios.get(`${getApiBaseUrl()}lost-found/analytics/summary`, {
    headers: authHeaders(token),
    params,
  });
  return response.data;
};
